import { Box, Flex, Text } from '@mantine/core';
import { Fragment, useCallback, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import {
  BORDER_COLOR,
  CELL_PADDING,
  FONT_SIZE,
  HEADER_BG,
  INDENT_SIZE,
  LEFT_SECTION_WIDTH,
  LINE_HEIGHT,
  TEXT_COLOR,
  useTableStyles
} from '../tableConstants';
import { getColumnEntries, parseQuestionDisplayName, resolveColumnValue } from '../tableUtils';
import { useCellNavigation } from '../useCellNavigation';
import { SingleFundHeaderRow } from '../components/SingleFundHeaderRow';
import { MultiFundRow } from '../components/MultiFundRow';
import { SingleFundRow } from '../components/SingleFundRow';
import { QuestionHeader } from '../components/QuestionHeader';
import { idCreator } from '../../../../../Project/utils/idCreator';

const MultiFundQuestion = ({
  question,
  schema,
  funds,
  fundKeys,
  tableName,
  levelIndex,
  handleCellChange,
  cellRefs,
  onCellKeyDown,
  getValue
}) => {
  const { columns, children, level, name } = question;
  const columnEntries = getColumnEntries(columns);
  const keyPrefix = question.level ?? question.name;
  const { classes } = useTableStyles();
  return (
    <>
      <Flex
        id={`formpf-group-multi-strip-${idCreator(`${tableName}-${name}`)}`}
        className={classes.borderBottom}
      >
        <Flex
          id={`formpf-group-multi-strip-left-${idCreator(`${tableName}-${name}`)}`}
          maw={LEFT_SECTION_WIDTH}
          miw={LEFT_SECTION_WIDTH}
          className={classes.leftSticky}
        >
          {level && (
            <Text
              id={`formpf-group-multi-strip-level-${idCreator(`${tableName}-${name}`)}`}
              p={CELL_PADDING}
              bg={HEADER_BG}
              ta="center"
              c={TEXT_COLOR}
              fz={FONT_SIZE}
              lh={LINE_HEIGHT}
              className={classes.borderRight}
              miw={INDENT_SIZE}
              maw={INDENT_SIZE}
            >
              {level}
            </Text>
          )}
          <Text
            id={`formpf-group-multi-strip-name-${idCreator(`${tableName}-${name}`)}`}
            p={CELL_PADDING}
            bg={HEADER_BG}
            flex="auto"
            c={TEXT_COLOR}
            fz={FONT_SIZE}
            lh={LINE_HEIGHT}
            className={classes.borderRight}
          >
            {name}
          </Text>
        </Flex>
        <Text
          id={`formpf-group-multi-strip-right-${idCreator(`${tableName}-${name}`)}`}
          p={CELL_PADDING}
          bg={HEADER_BG}
          flex="auto"
        ></Text>
      </Flex>
      {!children &&
        columnEntries.map(([columnKey, columnSchema]) => (
          <MultiFundRow
            key={`${keyPrefix}-${columnKey}`}
            columnSchema={columnSchema}
            cellSchema={schema.properties[columnKey]}
            levelIndex={levelIndex}
            fundKeys={fundKeys}
            getValue={fundKey => getValue(question.value, fundKey)}
            getError={fundKey => getValue(question.value, fundKey)?.errors?.[columnKey]}
            onFundChange={(fundKey, value) =>
              handleCellChange(columnKey, value, fundKey, question.value)
            }
            cellRefs={cellRefs}
            getCellKey={fundKey => `${keyPrefix}-${columnKey}-${fundKey}`}
            onCellKeyDown={onCellKeyDown}
            funds={funds}
            tableName={tableName}
            fieldName={columnKey}
            hasDrillDownFeature={schema.properties[columnKey]?.has_drill_down_feature}
            getTableId={fundKey => getValue(question.value, fundKey)?.['id']}
            getReportId={fundKey => getValue(question.value, fundKey)?.['report_id']}
            isFromStaticTemplate={fundKey =>
              getValue(question.value, fundKey)?.['static_template_fields']?.includes(columnKey)
            }
            symbol={schema.properties[columnKey]?.symbol}
          />
        ))}

      {children &&
        children.map(childQuestion => (
          <Fragment key={`${keyPrefix}-${childQuestion.level ?? childQuestion.name}`}>
            <MultiFundQuestion
              question={childQuestion}
              schema={schema}
              funds={funds}
              fundKeys={fundKeys}
              tableName={tableName}
              levelIndex={levelIndex + 1}
              handleCellChange={handleCellChange}
              cellRefs={cellRefs}
              onCellKeyDown={onCellKeyDown}
              getValue={getValue}
            />
          </Fragment>
        ))}
    </>
  );
};

const SingleFundQuestion = ({
  question,
  schema,
  levelIndex,
  handleCellChange,
  cellRefs,
  onCellKeyDown,
  getValue,
  fund,
  tableName
}) => {
  const { columns, children, level, name } = question;
  const columnEntries = getColumnEntries(columns);
  const keyPrefix = question.level ?? question.name;

  return (
    <>
      <SingleFundHeaderRow levelIndex={levelIndex} level={level} name={name} />
      {!children &&
        columnEntries.map(([columnKey, columnSchema]) => (
          <SingleFundRow
            key={`${keyPrefix}-${columnKey}`}
            columnSchema={columnSchema}
            cellSchema={schema.properties[columnKey]}
            levelIndex={levelIndex}
            value={resolveColumnValue(getValue(question.value), columnKey)}
            error={getValue(question.value)?.errors?.[columnKey]}
            onChange={value => handleCellChange(columnKey, value, null, question.value)}
            cellRefs={cellRefs}
            cellKey={`${keyPrefix}-${columnKey}`}
            onCellKeyDown={onCellKeyDown}
            fundId={fund?.fund_id}
            fundName={fund?.fund_name}
            tableName={tableName}
            fieldName={columnKey}
            tableId={getValue(question.value)?.['id']}
            reportId={getValue(question.value)?.['report_id']}
            hasDrillDownFeature={schema.properties[columnKey]?.has_drill_down_feature}
            isFromStaticTemplate={getValue(question.value)?.['static_template_fields']?.includes(
              columnKey
            )}
            symbol={schema.properties[columnKey]?.symbol}
          />
        ))}

      {children &&
        children.map(childQuestion => (
          <Fragment key={`${keyPrefix}-${childQuestion.level ?? childQuestion.name}`}>
            <SingleFundQuestion
              question={childQuestion}
              schema={schema}
              levelIndex={levelIndex + 1}
              handleCellChange={handleCellChange}
              cellRefs={cellRefs}
              onCellKeyDown={onCellKeyDown}
              getValue={getValue}
              fund={fund}
              tableName={tableName}
            />
          </Fragment>
        ))}
    </>
  );
};

export const GroupTable = ({
  schema,
  fundId,
  isMultiFunds,
  funds,
  tableName,
  onChangeFund,
  name,
  questionLevels = [],
  groupColumn,
  virtualized = false,
  isQuestionOpen = true,
  onToggleQuestion
}) => {
  const fundKeys = useMemo(() => Object.keys(funds || {}), [funds]);

  const { cellRefs, registerCell, onCellKeyDown } = useCellNavigation({ isMultiFunds, fundKeys });

  const groupDataByFund = useMemo(() => {
    // When question content is collapsed, only render header and skip heavy map/index building.
    if (!isQuestionOpen) return {};

    return fundKeys.reduce((acc, key) => {
      const rows = funds?.[key]?.fund_data?.[tableName] || [];
      const rowByGroup = new Map();
      const indexByGroup = new Map();

      rows.forEach((row, index) => {
        rowByGroup.set(row?.[groupColumn], row);
        indexByGroup.set(row?.[groupColumn], index);
      });

      acc[key] = {
        rowByGroup,
        indexByGroup
      };

      return acc;
    }, {});
  }, [fundKeys, funds, tableName, groupColumn, isQuestionOpen]);

  const handleCellChange = useCallback(
    (columnKey, value, targetFundId, groupValue) => {
      const resolvedFundId = isMultiFunds ? targetFundId : fundId;
      if (resolvedFundId === undefined || resolvedFundId === null) {
        return;
      }

      const currentRowIndex =
        groupDataByFund?.[resolvedFundId]?.indexByGroup?.get(groupValue) ?? -1;
      if (currentRowIndex < 0) return;

      const tableRows = funds?.[resolvedFundId]?.fund_data?.[tableName];
      if (!Array.isArray(tableRows)) return;
      if (tableRows[currentRowIndex]?.[columnKey] === value) return;

      const newTable = tableRows.map((row, i) =>
        i === currentRowIndex ? { ...row, [columnKey]: value } : row
      );

      onChangeFund(tableName, newTable, isMultiFunds ? targetFundId : null);
    },
    [fundId, isMultiFunds, onChangeFund, tableName, funds, groupDataByFund]
  );

  const getMultiFundValue = useCallback(
    (groupValue, currentFundId) => groupDataByFund?.[currentFundId]?.rowByGroup?.get(groupValue),
    [groupDataByFund]
  );

  const getSingleFundValue = useCallback(
    groupValue => groupDataByFund?.[fundId]?.rowByGroup?.get(groupValue),
    [groupDataByFund, fundId]
  );

  const { questionNumber, questionName } = parseQuestionDisplayName(name);

  if (isMultiFunds) {
    const content = index => {
      // 0-й элемент — шапка, дальше вопросы
      if (index === 0) {
        return (
          <QuestionHeader
            key={`${tableName}-header`}
            name={questionName}
            number={questionNumber}
            isMultiFunds={isMultiFunds}
            fundKeys={fundKeys}
            funds={funds}
            isOpen={isQuestionOpen}
            onToggle={onToggleQuestion}
          />
        );
      }

      const questionIndex = index - 1;
      const question = questionLevels[questionIndex];
      if (!question) return null;

      return (
        <Fragment key={`${tableName}-${questionIndex}-multi-view`}>
          <MultiFundQuestion
            question={question}
            schema={schema}
            funds={funds}
            fundKeys={fundKeys}
            tableName={tableName}
            levelIndex={0}
            handleCellChange={handleCellChange}
            cellRefs={{ current: cellRefs, register: registerCell }}
            onCellKeyDown={onCellKeyDown}
            getValue={getMultiFundValue}
          />
        </Fragment>
      );
    };

    const isWidthFit =
      fundKeys.length > 3 && window.innerWidth < fundKeys.length * 260 + LEFT_SECTION_WIDTH;

    return (
      <Flex
        id={`formpf-group-table-${idCreator(tableName)}-multi`}
        direction="column"
        bd={`1px solid ${BORDER_COLOR}`}
      >
        {virtualized && !isQuestionOpen ? (
          <QuestionHeader
            id={`formpf-group-table-header-${idCreator(tableName)}-multi`}
            key={`${tableName}-header`}
            name={questionName}
            number={questionNumber}
            isMultiFunds={isMultiFunds}
            fundKeys={fundKeys}
            funds={funds}
            isOpen={isQuestionOpen}
            onToggle={onToggleQuestion}
          />
        ) : virtualized ? (
          <Virtuoso
            style={{ width: '100%', height: 400 }}
            totalCount={isQuestionOpen ? questionLevels.length + 1 : 1}
            itemContent={content}
            overscan={3}
            components={{
              Item: ({ children, ...props }) => (
                <div
                  {...props}
                  style={{ ...(props.style || {}), width: isWidthFit && 'min-content' }}
                >
                  {children}
                </div>
              )
            }}
          />
        ) : (
          <>
            <QuestionHeader
              id={`formpf-group-table-header-${idCreator(tableName)}-multi`}
              number={questionNumber}
              name={questionName}
              isMultiFunds={isMultiFunds}
              fundKeys={fundKeys}
              funds={funds}
              isOpen={isQuestionOpen}
              onToggle={onToggleQuestion}
            />
            {isQuestionOpen &&
              questionLevels.map((question, questionIndex) => (
                <Fragment key={`${tableName}-${questionIndex}-multi-view`}>
                  <MultiFundQuestion
                    question={question}
                    schema={schema}
                    funds={funds}
                    fundKeys={fundKeys}
                    tableName={tableName}
                    levelIndex={0}
                    handleCellChange={handleCellChange}
                    cellRefs={{ current: cellRefs, register: registerCell }}
                    onCellKeyDown={onCellKeyDown}
                    getValue={getMultiFundValue}
                  />
                </Fragment>
              ))}
          </>
        )}
      </Flex>
    );
  }

  const singleContent = index => {
    const question = questionLevels[index];
    if (!question) return null;

    return (
      <Fragment key={`${tableName}-${index}`}>
        <SingleFundQuestion
          question={question}
          schema={schema}
          levelIndex={0}
          handleCellChange={handleCellChange}
          cellRefs={{ current: cellRefs, register: registerCell }}
          onCellKeyDown={onCellKeyDown}
          getValue={getSingleFundValue}
          fund={funds?.[fundId]}
          tableName={tableName}
        />
      </Fragment>
    );
  };

  return (
    <Flex
      id={`formpf-group-table-${idCreator(tableName)}-single`}
      direction="column"
      bd={`1px solid ${BORDER_COLOR}`}
    >
      <QuestionHeader
        id={`formpf-group-table-header-${idCreator(tableName)}-single`}
        number={questionNumber}
        name={questionName}
        isMultiFunds={isMultiFunds}
        isOpen={isQuestionOpen}
        onToggle={onToggleQuestion}
      />
      {isQuestionOpen && virtualized ? (
        <Box
          sx={() => ({
            width: '100%',
            maxHeight: 400
          })}
        >
          <Virtuoso
            style={{ width: '100%', height: 400 }}
            totalCount={questionLevels.length}
            itemContent={singleContent}
            overscan={3}
          />
        </Box>
      ) : isQuestionOpen ? (
        questionLevels.map((question, questionIndex) => (
          <Fragment key={`${tableName}-${questionIndex}`}>
            <SingleFundQuestion
              question={question}
              schema={schema}
              levelIndex={0}
              handleCellChange={handleCellChange}
              cellRefs={{ current: cellRefs, register: registerCell }}
              onCellKeyDown={onCellKeyDown}
              getValue={getSingleFundValue}
              fund={funds?.[fundId]}
              tableName={tableName}
            />
          </Fragment>
        ))
      ) : null}
    </Flex>
  );
};
